import React from 'react';
import { SuccessResponse } from '@uppy/core';
import _noop from 'lodash/noop';
import _uniq from 'lodash/uniq';

import { Uppy, UppyFile } from '@lyearn/shared/hooks/useUppy';

import { isRemoteUrl, throwIfError, validatedSrc } from '../utils/upload';

const FILE_IDENTIFIER_KEY = 'ly-index';

const useRemoteUpload = (uppy: Uppy | null, onChange: (oldUrl: string, newUrl: string) => void) => {
  const sources = React.useRef(new Set<string>([]));

  const onUploadComplete = React.useRef(_noop);
  React.useEffect(() => {
    onUploadComplete.current = (sourceUrl: string, uploadUrl: string) => {
      if (sources.current.has(sourceUrl)) {
        onChange(sourceUrl, uploadUrl);
      }
    };
  }, [onChange]);

  React.useEffect(() => {
    const uploadSuccess = (file?: UppyFile, body?: SuccessResponse) => {
      const sourceUrl = file?.remote?.body?.url as string;
      if (sourceUrl) {
        const url = new URL(sourceUrl);
        url.searchParams.delete(FILE_IDENTIFIER_KEY);
        onUploadComplete.current(url.toString(), body?.uploadURL);
      }
    };

    uppy?.on('upload-success', uploadSuccess);

    return () => {
      uppy?.off('upload-success', uploadSuccess);
    };
  }, [uppy]);

  const addToUpload = React.useCallback(
    (_srcList?: string[]) => {
      const newSources = _uniq(
        _srcList?.filter(
          (src) => validatedSrc(src) && isRemoteUrl(src) && !sources.current.has(src),
        ),
      );

      if (!newSources.length) {
        return;
      }

      const urlPlugin = uppy?.getPlugin('Url');

      newSources.map((src, index) => {
        const url = new URL(src);
        sources.current.add(src.toString());
        // all urls may same file name so uppy will think those as same file
        url.searchParams.append(FILE_IDENTIFIER_KEY, index.toString());

        // @ts-ignore
        return urlPlugin?.addFile(url.toString()).then(throwIfError);
      });
    },
    [uppy],
  );

  return { addToUpload };
};

export default useRemoteUpload;
